import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, ValidationErrors } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataModelChangeEvent } from '../../interfaces/odata-model.interface';
import { BooleanModel } from '../../models/primitive/boolean.model';
@Component({
    selector: 'pace-core-checkbox-input',
    templateUrl: './checkbox-input.component.html',
    styleUrls: ['./checkbox-input.component.scss'],
})
export class CheckboxInputComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public model: DisplayDecorator<BooleanModel>;

    @Input()
    public isReadonlyInputControl: boolean;

    @Input()
    public ariaLabel = 'Checkbox Input';

    @Output()
    public modelChange = new EventEmitter<DisplayDecorator<BooleanModel>>();

    @Input()
    public parentForm: FormArray | undefined;

    @Input()
    public checkBoxID = 'checkbox-input-field';

    @Output()
    public valueChange = new EventEmitter<ODataModelChangeEvent<BooleanModel>>();

    public checkboxControl: FormControl;

    public faCheck = faCheck;

    private destroySubject = new Subject<void>();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly formBuilder: FormBuilder,
    ) {}

    public ngOnInit(): void {
        const checkboxValidator = (control: AbstractControl): ValidationErrors | null => this.checkValidity(control);

        this.checkboxControl = this.formBuilder.control(
            { value: this.model.value, disabled: this.model.options.disabled },
            checkboxValidator,
        );

        this.checkboxControl.valueChanges
            .pipe(
                distinctUntilChanged(),
                filter((newVal: boolean) => newVal !== this.model.value),
                takeUntil(this.destroySubject),
            )
            .subscribe((newVal: boolean) => {
                const oldValue = this.model.value;
                this.model.value = newVal;
                this.modelChange.emit(this.model);
                this.valueChange.emit({
                    oldValue: oldValue,
                    newValue: this.model.value,
                });
            });

        // The actual control needs to be added asynchronously to avoid an ExpressionChangedAfterItHasBeenCheckedError
        // See also https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.checkboxControl);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'Error in CheckboxInputComponent -> ngOnInit -> parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj && changesObj.model && this.checkboxControl) {
            if (this.model.options.disabled && this.checkboxControl.enabled) {
                this.checkboxControl.disable({ onlySelf: true, emitEvent: false });
            }
            if (!this.model.options.disabled && this.checkboxControl.disabled) {
                this.checkboxControl.enable({ onlySelf: true, emitEvent: false });
            }

            this.model = <DisplayDecorator<BooleanModel>>changesObj.model.currentValue;
            this.checkboxControl.setValue(this.model.value);
        }
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.checkboxControl);
            this.parentForm.removeAt(index);
        }
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    public checkValidity(control: AbstractControl): ValidationErrors | null {
        if (this.model.options.required && !control.value) {
            return { required: 'Checkbox is not checked' };
        }

        return null;
    }
}
